import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Zap, ArrowLeft, Trophy, History, Heart, Play, RotateCcw, AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const COLORS = [
  { name: 'RED', hex: '#ef4444', label: 'RED' },
  { name: 'GREEN', hex: '#22c55e', label: 'GREEN' },
  { name: 'BLUE', hex: '#3b82f6', label: 'BLUE' },
  { name: 'YELLOW', hex: '#eab308', label: 'YELLOW' },
  { name: 'PURPLE', hex: '#a855f7', label: 'PURPLE' }
];

export default function TwitchReflexPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Game State
  const [gameState, setGameState] = useState('intro'); // intro, idle, waiting, sequence, result, gameover
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [targetColor, setTargetColor] = useState(null);
  const [currentColor, setCurrentColor] = useState(null); // The color currently flashing
  const [targetDuration, setTargetDuration] = useState(2000); // Start at 2s
  const [message, setMessage] = useState("Click to Start");
  
  // Data
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Refs
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(0);
  const sequenceRunningRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    fetchLeaderboard();
    fetchAttempts();
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/twitch-reflex/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/twitch-reflex/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const getTargetDuration = (lvl) => {
    // From 2s down to 0.05s. Reduce by 0.1s (100ms) per level.
    // L1: 2000, L2: 1900, ... L20: 100, L21+: 50
    const duration = 2000 - ((lvl - 1) * 100);
    return Math.max(50, duration);
  };

  const startGame = () => {
    setScore(0);
    setLives(3);
    setLevel(1);
    setGameState('idle');
    setMessage("Ready?");
  };

  const startLevel = () => {
    if (gameState !== 'idle') return;
    
    const duration = getTargetDuration(level);
    setTargetDuration(duration);
    
    // Pick Target Color
    const target = COLORS[Math.floor(Math.random() * COLORS.length)];
    setTargetColor(target);
    
    setGameState('waiting');
    setMessage(`Click when you see ${target.label}`);
    
    // Start sequence after short delay
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      runSequence(target, duration);
    }, 2000);
  };

  const runSequence = (target, duration) => {
    setGameState('sequence');
    sequenceRunningRef.current = true;
    
    // Decide if NEXT color is target or distractor
    // We want random distractors before target
    // Logic: Recursive loop. In each step, random chance to show target?
    // Or just queue random distractors?
    // Let's do: 
    // Loop distractors. Each step has X% chance to be target.
    // Distractor duration: 0.5 - 2.5s (random)
    
    const showNext = () => {
      if (!sequenceRunningRef.current) return;

      // 30% chance to show target, or guaranteed if we've shown too many distractors?
      // Let's make it random but fair.
      const isTarget = Math.random() < 0.3; 
      
      if (isTarget) {
        // SHOW TARGET
        setCurrentColor(target);
        startTimeRef.current = performance.now();
        
        // Window to click
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          // If this timeout fires, user missed it
          handleMiss();
        }, duration);
        
      } else {
        // SHOW DISTRACTOR
        let distractor;
        do {
          distractor = COLORS[Math.floor(Math.random() * COLORS.length)];
        } while (distractor.name === target.name);
        
        setCurrentColor(distractor);
        
        const distractorTime = 500 + Math.random() * 2000; // 0.5 - 2.5s
        
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          showNext();
        }, distractorTime);
      }
    };

    showNext();
  };

  const handleInteraction = (e) => {
    if (e.type === 'touchstart') {
        // e.preventDefault(); 
    }

    if (gameState === 'intro' || gameState === 'gameover') return;

    if (gameState === 'idle') {
      startLevel();
      return;
    }

    if (gameState === 'waiting') {
        return; // Ignore clicks during instruction
    }

    if (gameState === 'sequence') {
        // Check what is currently showing
        if (currentColor && targetColor && currentColor.name === targetColor.name) {
            // SUCCESS
            handleSuccess();
        } else {
            // FALSE START (Clicked on distractor)
            handleFalseStart();
        }
    }
    
    if (gameState === 'result') {
        // Continue to next level
        setGameState('idle');
        setMessage("Ready?");
    }
  };

  const handleSuccess = () => {
    sequenceRunningRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    const rt = performance.now() - startTimeRef.current;
    
    // Scoring
    const basePoints = level * 100;
    // Bonus based on how fast within the window they clicked
    // Window is targetDuration.
    const bonus = Math.floor((targetDuration - rt) / 10); 
    const roundScore = basePoints + Math.max(0, bonus);
    
    setScore(prev => prev + roundScore);
    setLevel(prev => prev + 1);
    
    setGameState('result');
    setMessage(`${rt.toFixed(0)}ms! Nice!`);
    setCurrentColor({ name: 'SUCCESS', hex: '#22c55e' }); // Green feedback
    toast.success(`Level ${level} Complete!`);
  };

  const handleMiss = () => {
    sequenceRunningRef.current = false;
    const newLives = lives - 1;
    setLives(newLives);
    
    if (newLives <= 0) {
        endGame();
    } else {
        setGameState('result');
        setMessage("Too Slow!");
        setCurrentColor({ name: 'FAIL', hex: '#ef4444' }); // Red feedback
        toast.error("Missed it! -1 Life");
    }
  };

  const handleFalseStart = () => {
    sequenceRunningRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    const newLives = lives - 1;
    setLives(newLives);
    
    if (newLives <= 0) {
        endGame();
    } else {
        setGameState('result');
        setMessage("Wrong Color!");
        setCurrentColor({ name: 'FAIL', hex: '#ef4444' }); // Red feedback
        toast.error("Don't click distractors! -1 Life");
    }
  };

  const endGame = async () => {
    setGameState('gameover');
    if (user) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/twitch-reflex/results`, {
          score: score,
          level_reached: level
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchLeaderboard();
        fetchAttempts();
      } catch (e) {
        console.error("Failed to save score", e);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-yellow-500/30">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex gap-6 font-mono text-sm">
           <div className="flex items-center gap-2">
             <Trophy className="w-4 h-4 text-yellow-500" />
             <span className="text-xl font-bold">{score}</span>
           </div>
           <div className="flex items-center gap-2 text-red-500">
             <Heart className="w-4 h-4 fill-current" />
             <span className="text-xl font-bold">{lives}</span>
           </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
                {/* Viewport Container */}
                <div 
                    onMouseDown={handleInteraction}
                    onTouchStart={handleInteraction}
                    className={`
                        flex flex-col items-center justify-center p-6 relative w-full rounded-3xl border min-h-[500px] transition-colors duration-100 cursor-pointer select-none
                        ${gameState === 'intro' ? 'bg-slate-900/30 border-slate-800/50' : ''}
                        ${gameState === 'gameover' ? 'bg-slate-900 border-slate-800' : ''}
                        ${gameState === 'idle' ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : ''}
                        ${gameState === 'waiting' ? 'bg-slate-900 border-slate-800' : ''}
                        ${gameState === 'sequence' ? '' : ''}
                    `}
                    style={{
                        backgroundColor: gameState === 'sequence' && currentColor ? currentColor.hex : 
                                         gameState === 'result' && currentColor ? currentColor.hex : undefined
                    }}
                >
                    
                    {/* Intro State */}
                    {gameState === 'intro' && (
                        <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500 pointer-events-none">
                            <div className="w-24 h-24 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-yellow-500/20">
                                <Zap className="w-12 h-12 text-white" />
                            </div>
                            <div>
                                <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">THE TWITCH REFLEX</h1>
                                <p className="text-slate-400 text-lg max-w-xs mx-auto">
                                    Wait for your Color. <br/>
                                    Ignore the rest.
                                </p>
                            </div>
                            <Button onClick={(e) => { e.stopPropagation(); startGame(); }} className="w-full py-8 text-xl font-bold bg-white text-black hover:bg-slate-200 rounded-xl uppercase tracking-wider shadow-xl shadow-white/10 pointer-events-auto">
                                Start Test
                            </Button>
                        </div>
                    )}

                    {/* Game Over State */}
                    {gameState === 'gameover' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-500 pointer-events-none">
                            <div className="text-6xl mb-4">💀</div>
                            <h2 className="text-4xl font-black">GAME OVER</h2>
                            
                            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 w-full max-w-xs mx-auto space-y-4">
                                <div>
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Final Score</div>
                                    <div className="text-5xl font-mono font-bold text-white">{score}</div>
                                </div>
                                <div className="border-t border-slate-800 pt-4">
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Level Reached</div>
                                    <div className="text-2xl font-mono font-bold text-yellow-500">{level}</div>
                                </div>
                            </div>

                            <Button onClick={(e) => { e.stopPropagation(); startGame(); }} className="w-full py-6 px-8 text-lg font-bold bg-white text-black hover:bg-slate-200 pointer-events-auto">
                                <RotateCcw className="w-5 h-5 mr-2" /> Play Again
                            </Button>
                        </div>
                    )}

                    {/* Active Game Messages */}
                    {(gameState !== 'intro' && gameState !== 'gameover') && (
                        <div className="text-center pointer-events-none relative z-10">
                            {/* HUD */}
                            <div className="absolute -top-24 left-0 w-full px-6 flex justify-between text-xs font-mono uppercase tracking-widest opacity-70">
                                <div>Level {level}</div>
                                <div>Window: {targetDuration}ms</div>
                            </div>

                            <div className={`text-5xl sm:text-7xl font-black tracking-tight mb-2 ${gameState === 'sequence' || gameState === 'result' ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                                {message}
                            </div>
                            
                            {gameState === 'waiting' && targetColor && (
                                <div className="mt-8">
                                    <div className="text-xl uppercase font-bold text-slate-400 mb-2">TARGET:</div>
                                    <div 
                                        className="w-32 h-32 rounded-2xl shadow-2xl mx-auto border-4 border-white/20 animate-pulse"
                                        style={{ backgroundColor: targetColor.hex }}
                                    />
                                </div>
                            )}
                            
                            {gameState === 'result' && (
                                <div className="text-white/80 text-lg mt-4 animate-bounce font-bold">
                                    Click to Continue
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-yellow-500">{entry.score}</div>
                            <div className="text-xs text-slate-500">Lvl {entry.level_reached}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => {
                        return (
                          <div 
                            key={attempt.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-sm">
                                {attempt.created_at ? new Date(attempt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                              </span>
                            </div>
                            <div className="text-right">
                                <div className="font-mono font-bold text-yellow-500">{attempt.score}</div>
                                <div className="text-xs text-slate-500">Lvl {attempt.level_reached}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
